import { ReactNode, useEffect, useRef } from "react";
import StyledSelect, { StyledSelectProps } from "./styles";
import { CustomState } from "../../Hooks/State.hook";
import { FaAngleDown, FaAngleUp, FaXmark } from "react-icons/fa6";
import removeAccents from "../../Hooks/removeAccents.util";

export type Option<T> = {
	name: string;
	value: T;
	key?: string | number;
};

type SelectOptions<T> = {
	options: Array<Option<T>>;
	defaultValue?: Option<T>;
	onChange?: (value: T | null) => unknown;
	placeholder?: string;
	disabled?: boolean;
	disableClearButton?: boolean;
	disableInput?: boolean;
	leftIcon?: ReactNode;
	className?: string;
};

type SelectProps<T> = SelectOptions<T> & Omit<StyledSelectProps, "$icons">;

function Select<T>({
	$padding,
	$width,
	$fontSize,
	$fontWeight,
	$color,
	$borderColor,
	$focusColor,
	$maxULHeigth,
	$disableBorder,
	options,
	onChange,
	placeholder,
	defaultValue,
	disabled,
	disableClearButton,
	disableInput,
	className,
	leftIcon,
}: SelectProps<T>) {
	const optionState = CustomState<Option<T> | null>(defaultValue || null);
	const displayMenuState = CustomState<boolean>(false);
	const inputValue = CustomState<string>("");
	const filteredOptions =
		optionState.value?.name === inputValue.value
			? options
			: options.filter((o) =>
				String(removeAccents(o.name?.toLowerCase())).includes(removeAccents(inputValue.value?.toLowerCase()))
			);

	const inputRef = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		if (optionState.value !== null) {
			onChange && onChange(optionState.value.value);
			inputValue.set(optionState.value.name);
		} else {
			onChange && onChange(null);
			inputValue.set("");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [optionState.value]);

	useEffect(() => {
		if (defaultValue) {
			optionState.set(defaultValue);
			inputValue.set(defaultValue?.name);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleChange = (option: Option<T>) => {
		return () => {
			optionState.set(option);
			displayMenuState.reset();
		};
	};

	const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		inputValue.set(e.target.value);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Tab") {
			e.preventDefault();
			const matchingOption =
				options.find((o) =>
					removeAccents(o.name.toLowerCase()).startsWith(removeAccents(inputValue.value.toLowerCase()))
				) ||
				options.find((o) =>
					removeAccents(o.name.toLowerCase()).includes(removeAccents(inputValue.value.toLowerCase()))
				);

			if (matchingOption) {
				optionState.set(matchingOption);
				inputValue.set(matchingOption.name);
			}
		}

		if (e.key === "Enter") {
			e.preventDefault();
			const matchingOption =
				options.find((o) =>
					removeAccents(o.name.toLowerCase()).startsWith(removeAccents(inputValue.value.toLowerCase()))
				) ||
				options.find((o) =>
					removeAccents(o.name.toLowerCase()).includes(removeAccents(inputValue.value.toLowerCase()))
				);

			if (matchingOption) {
				optionState.set(matchingOption);
				inputValue.set(matchingOption.name);
				inputRef.current?.blur();
			}
		}
	};

	const handleInputFocus = (_: React.FocusEvent<HTMLInputElement, Element>) => {
		displayMenuState.set(true);
	};

	const handleInputBlur = (_: React.FocusEvent<HTMLInputElement, Element>) => {
		setTimeout(() => {
			displayMenuState.set(false);
		}, 200);
	};

	return (
		<StyledSelect
			{...{
				$padding,
				$width,
				$fontSize,
				$fontWeight,
				$color,
				$borderColor,
				$focusColor,
				$maxULHeigth,
				$disableBorder,
				$disabled: disabled,
				$leftIcon: !!leftIcon,
			}}
			className={className}
		>
			<div>
				{leftIcon}
				<input
					placeholder={placeholder}
					value={inputValue.value as string}
					onChange={handleChangeInput}
					onKeyDown={handleKeyDown}
					onFocus={handleInputFocus}
					onBlur={handleInputBlur}
					ref={inputRef}
					disabled={disabled || disableInput}
				/>
				{!disabled && (
					<div className="icons">
						{!disableClearButton && optionState.value && <FaXmark onClick={() => optionState.set(null)} />}
						{displayMenuState.value ? (
							<FaAngleUp onClick={() => inputRef.current?.blur()} />
						) : (
							<FaAngleDown onClick={() => inputRef.current?.focus()} />
						)}
					</div>
				)}
			</div>
			<ul className={!disabled && displayMenuState.value ? "showing" : "hidden"}>
				{filteredOptions.map((option) => {
					const key = option.key || (["string", "number"].includes(typeof option.value) ? option.value : option.name);
					return (<li
						className={optionState.value === option.value ? "selected" : ""}
						onClick={handleChange(option)}
						key={`option_${key}`}
					>
						<p> {option.name} </p>
					</li>)
				})}
			</ul>
		</StyledSelect>
	);
}

export default Select;
